import { useCallback, useMemo } from "react";

import { Modal } from "@similarweb/ui-components";

import { ContentStateRenderer } from "app/components/PackageSwitcher/components/ContentStateRenderer/ContentStateRenderer";
import { SwitchPackageStage } from "app/components/PackageSwitcher/components/SwitchPackageStage/SwitchPackageStage";
import type { FeatureArea } from "app/components/PackageSwitcher/consts/packageFeatureInfo";
import type { PaymentPeriod } from "app/components/PackageSwitcher/consts/paymentPeriod";
import { TrackingAction } from "app/components/PackageSwitcher/consts/TrackingActions";
import type { IAddOn } from "app/components/PackageSwitcher/types/addonsTypes";
import { useSubmitPackageChange } from "app/custom-hooks/switchPackage/useSubmitPackageChange";
import type { IGetCurrentPackageSwitchInformationResponse } from "app/custom-hooks/switchPackage/useSwitchPackagePopup.types";
import { allTrackers } from "app/services/track/track";

import type { ISwitchPackageTrackingInformation } from "../types/switchPackageTracking.types";

import { PackageSwitcher } from "./PackageSwitcher/PackageSwitcher";
import { SwitchPackagePopup, switchPackageModalStyles } from "./PopUpSwitchPackage.styled";

interface IPopUpSwitchPackage {
    switchInformationData: IGetCurrentPackageSwitchInformationResponse | null;
    isError: boolean;
    isLoading: boolean;
    setIsPopUpOpen: (isOpen: boolean) => void;
    targetPackagePeriod: PaymentPeriod;
    availableAddOns: IAddOn[];
    featureArea: FeatureArea;
    hookTrackingKey: string;
}

export const PopUpSwitchPackage = ({
    switchInformationData,
    isError,
    isLoading,
    setIsPopUpOpen,
    targetPackagePeriod,
    availableAddOns,
    featureArea,
    hookTrackingKey,
}: IPopUpSwitchPackage) => {
    const packageToChange = useMemo(() => {
        return switchInformationData?.switchOption.find((option) => option.period === targetPackagePeriod);
    }, [switchInformationData, targetPackagePeriod]);

    const trackingInformation = useMemo((): ISwitchPackageTrackingInformation | undefined => {
        if (!switchInformationData || !packageToChange) {
            return undefined;
        }

        return {
            targetProductName: packageToChange.package,
            hookTrackingKey,
        };
    }, [hookTrackingKey, packageToChange, switchInformationData]);

    const {
        isInProcessOfPackageSwitching,
        switchPackageStageData,
        shouldCloseModalOnEsc,
        handleSubmitButtonClick,
        onModalActionSuccess,
    } = useSubmitPackageChange({
        packageToChange,
        trackingInformation,
    });

    const trackSwitchPackageCloseEvent = useCallback(
        (closeType: "close X" | "cancel") => {
            if (!trackingInformation) {
                return;
            }
            allTrackers.dispatchTrackEvent(
                "one click package",
                TrackingAction.CLOSE,
                `${trackingInformation?.hookTrackingKey}+${packageToChange?.package}/${closeType}`,
            );
        },
        [packageToChange?.package, trackingInformation],
    );

    const onClose = useCallback((): void => {
        setIsPopUpOpen(false);
        trackSwitchPackageCloseEvent("close X");
    }, [setIsPopUpOpen, trackSwitchPackageCloseEvent]);

    const onCancelClick = useCallback((): void => {
        setIsPopUpOpen(false);
        trackSwitchPackageCloseEvent("cancel");
    }, [setIsPopUpOpen, trackSwitchPackageCloseEvent]);

    return (
        <Modal
            isOpen
            onRequestClose={onClose}
            customStyles={switchPackageModalStyles}
            shouldCloseOnEsc={shouldCloseModalOnEsc}
        >
            <SwitchPackagePopup data-automation="package-switcher-modal">
                {isInProcessOfPackageSwitching ? (
                    <SwitchPackageStage
                        switchPackageStageData={switchPackageStageData}
                        onClose={onClose}
                        onActionSuccess={onModalActionSuccess}
                        trackingInformation={trackingInformation}
                    />
                ) : (
                    <ContentStateRenderer
                        isError={isError}
                        isLoading={isLoading}
                        isDataLoaded={!!packageToChange}
                        onClose={onClose}
                        onCancelClick={onCancelClick}
                        onSubmitButtonClick={handleSubmitButtonClick}
                        packageTitle={packageToChange?.title}
                    >
                        {packageToChange && switchInformationData ? (
                            <PackageSwitcher
                                switchInformationData={switchInformationData}
                                packageToChange={packageToChange}
                                availableAddOns={availableAddOns}
                                featureArea={featureArea}
                            />
                        ) : null}
                    </ContentStateRenderer>
                )}
            </SwitchPackagePopup>
        </Modal>
    );
};
